import React, { useRef, useState } from "react";
import * as Mui from "@mui/material";
import * as XLSX from "xlsx";
import { FileDownloadOutlined, UploadFileOutlined } from "@mui/icons-material";
import { designColors, designRadii, designShell, designType, designWeights } from "services/constants/designTokens";
import { sharedTableBodyCellSx } from "components/DesignSystem/sharedUiPatterns";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { PanelActionFooter, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { panelBodySx } from "../Shared/formUi";
import { monthKey } from "./bonusCalc";
import { commitBonusImport, previewBonusImport } from "./bonusSampleApi";
import { Chip, SectionCard, downloadImportTemplate, forPeriod, money } from "./bonusUi";

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

// "2025-04", "Apr 2025", "April-2025", "04/2025" or an Excel date -> "YYYY-MM".
export const toMonth = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "number") {
    const date = XLSX.SSF.parse_date_code(value);
    return date ? monthKey(date.y, date.m - 1) : "";
  }
  const text = String(value).trim();
  let match = /^(\d{4})-(\d{1,2})(-\d{1,2})?$/.exec(text);
  if (match) return monthKey(Number(match[1]), Number(match[2]) - 1);
  match = /^(\d{1,2})[/-](\d{4})$/.exec(text);
  if (match) return monthKey(Number(match[2]), Number(match[1]) - 1);
  match = /^([A-Za-z]{3,9})[\s-]+(\d{2,4})$/.exec(text);
  if (match) {
    const index = MONTHS.indexOf(match[1].slice(0, 3).toLowerCase());
    const year = match[2].length === 2 ? 2000 + Number(match[2]) : Number(match[2]);
    if (index >= 0) return monthKey(year, index);
  }
  return text;
};

const pick = (row, ...names) => {
  const key = Object.keys(row).find((k) => names.includes(k.trim().toLowerCase()));
  return key === undefined ? "" : row[key];
};

const readFile = async (file) => {
  const book = XLSX.read(await file.arrayBuffer(), { type: "array" });
  const sheet = book.Sheets[book.SheetNames[0]];
  return XLSX.utils.sheet_to_json(sheet, { defval: "", raw: true }).map((row, index) => ({
    row: index + 2,
    employee_code: String(pick(row, "employee code", "employee_code", "code")).trim(),
    bonus_type: String(pick(row, "bonus type", "bonus_type")).trim(),
    period_from: toMonth(pick(row, "period from", "period_from", "from")),
    period_to: toMonth(pick(row, "period to", "period_to", "to")),
    amount: String(pick(row, "amount")).trim(),
    payroll_cycle: String(pick(row, "payroll cycle", "payroll_cycle", "cycle")).trim(),
    external_ref: String(pick(row, "external reference", "external_ref", "reference")).trim(),
    remarks: String(pick(row, "remarks")).trim(),
  })).filter((row) => row.employee_code || row.bonus_type || row.amount);
};

const GROUPS = [
  { key: "valid", label: "Valid", tone: "green" },
  { key: "invalid", label: "Invalid", tone: "red" },
  { key: "duplicate", label: "Duplicate", tone: "amber" },
];

const headSx = { ...designShell.tableHeadCellBase, py: 1, whiteSpace: "nowrap" };
const cellSx = { ...sharedTableBodyCellSx, ...designType.tableBodyCell, py: 1 };

// Import (spec B9): valid rows saved, invalid rows reported with the
// reason, already-saved rows shown as Duplicate and not saved again.
export const BonusImport = ({ companyId, types, treatment, onClose, onImported }) => {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState(null);
  const [group, setGroup] = useState("valid");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(true);

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setError("");
    setRows(null);
    try {
      const parsed = await readFile(file);
      if (!parsed.length) throw new Error("No rows found in the file. Use the template columns.");
      const preview = await previewBonusImport({ companyId, rows: parsed, types, treatment });
      setFileName(file.name);
      setRows(preview);
      setGroup(preview.some((row) => row.status === "valid") ? "valid" : preview.some((row) => row.status === "invalid") ? "invalid" : "duplicate");
    } catch (err) {
      setError(err.message || "Couldn't read the file.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const counts = Object.fromEntries(GROUPS.map((g) => [g.key, rows ? rows.filter((row) => row.status === g.key).length : 0]));
  const shown = rows ? rows.filter((row) => row.status === group) : [];
  const anyStatutory = rows?.some((row) => row.status === "valid" && row.is_statutory);

  const commit = async () => {
    if (busy || !counts.valid) return;
    setBusy(true);
    try {
      const result = await commitBonusImport({ companyId, rows, types, fileName });
      onImported({ saved: result.saved, invalid: counts.invalid, duplicate: counts.duplicate });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <GlobalSidePanel
      open
      expanded={expanded}
      onToggleExpand={() => setExpanded((value) => !value)}
      onClose={() => { if (!busy) onClose(); }}
      title="Import Bonus"
      subtitle="Employee code, Bonus type, Period from, Period to, Amount, Remarks. Payroll cycle is optional."
      bodySx={panelBodySx}
      footer={
        <PanelActionFooter
          onCancel={() => { if (!busy) onClose(); }}
          primaryLabel={busy && rows ? "Saving..." : `Save ${counts.valid} valid row${counts.valid === 1 ? "" : "s"}`}
          primaryProps={{ onClick: commit, disabled: busy || !counts.valid }}
        />
      }
    >
      <Mui.Stack spacing={2}>
        <SectionCard title="1. Upload the file">
          <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <SecondaryButton startIcon={<FileDownloadOutlined />} onClick={() => downloadImportTemplate(types)}>Download template</SecondaryButton>
            <SecondaryButton startIcon={<UploadFileOutlined />} disabled={busy} onClick={() => inputRef.current?.click()}>
              {fileName ? "Choose another file" : "Choose file (.xlsx, .csv)"}
            </SecondaryButton>
            <input ref={inputRef} type="file" hidden accept=".xlsx,.xls,.csv" onChange={(event) => onFile(event.target.files?.[0])} />
          </Mui.Stack>
          <Mui.Typography sx={{ ...designType.captionText, mt: 1.5 }}>
            Months as YYYY-MM or "Apr 2026". Rows with no payroll cycle follow the default treatment
            {treatment === "SELECT_CYCLE" ? " (Select payroll cycle — such rows are Invalid)" : treatment === "ON_HOLD" ? " (Defer — saved without a cycle)" : " (Current payroll cycle)"}.
            Uploading the same file again never saves a row twice.
          </Mui.Typography>
          {fileName ? <Mui.Typography sx={{ ...designType.tableBodyCell, mt: 1, fontWeight: designWeights.semibold }}>{fileName}</Mui.Typography> : null}
        </SectionCard>

        {error ? <Mui.Alert severity="error">{error}</Mui.Alert> : null}
        {busy && !rows ? <Mui.Box sx={{ textAlign: "center", py: 3 }}><Mui.CircularProgress size={24} sx={{ color: designColors.teal }} /></Mui.Box> : null}

        {rows ? (
          <SectionCard title="2. Check the rows">
            {anyStatutory ? <Mui.Box sx={{ mb: 1.5 }}><RequestInfoBanner tone="warning" body="Manual amounts are not checked against statutory eligibility or calculation." /></Mui.Box> : null}
            <Mui.Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap", rowGap: 1 }}>
              {GROUPS.map((g) => (
                <Mui.Button
                  key={g.key}
                  onClick={() => setGroup(g.key)}
                  sx={{
                    textTransform: "none",
                    borderRadius: designRadii.compactButton,
                    border: `1px solid ${group === g.key ? designColors.teal : designColors.slate200}`,
                    backgroundColor: group === g.key ? designColors.tealTint : designColors.white,
                    color: designColors.slate900,
                    px: 1.5,
                  }}
                >
                  <Chip label={g.label} tone={g.tone} sx={{ mr: 1 }} /> {counts[g.key]}
                </Mui.Button>
              ))}
            </Mui.Stack>
            <Mui.TableContainer sx={{ overflowX: "auto" }}>
              <Mui.Table size="small" sx={{ minWidth: 860 }}>
                <Mui.TableHead>
                  <Mui.TableRow>
                    {["Row", "Employee", "Bonus Type", "For Period", "Amount (₹)", "Payroll Cycle", group === "valid" ? "Remarks" : "Reason"].map((label) => (
                      <Mui.TableCell key={label} sx={headSx}>{label}</Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {shown.length === 0 ? (
                    <Mui.TableRow><Mui.TableCell colSpan={7} sx={{ ...cellSx, textAlign: "center", py: 3, color: designColors.slate600 }}>No rows here.</Mui.TableCell></Mui.TableRow>
                  ) : shown.map((row) => (
                    <Mui.TableRow key={row.row}>
                      <Mui.TableCell sx={cellSx}>{row.row}</Mui.TableCell>
                      <Mui.TableCell sx={cellSx}>{row.employee_name ? `${row.employee_name} (${row.employee_code})` : row.employee_code || "—"}</Mui.TableCell>
                      <Mui.TableCell sx={cellSx}>{row.bonus_type || "—"}</Mui.TableCell>
                      <Mui.TableCell sx={cellSx}>{/^\d{4}-\d{2}$/.test(row.period_from) && /^\d{4}-\d{2}$/.test(row.period_to) ? forPeriod(row.period_from, row.period_to) : `${row.period_from || "—"} – ${row.period_to || "—"}`}</Mui.TableCell>
                      <Mui.TableCell sx={{ ...cellSx, textAlign: "right" }}>{Number.isFinite(Number(row.amount)) && row.amount !== "" ? money(Number(row.amount)) : row.amount || "—"}</Mui.TableCell>
                      <Mui.TableCell sx={cellSx}>{row.payroll_cycle_name || "—"}</Mui.TableCell>
                      <Mui.TableCell sx={{ ...cellSx, whiteSpace: "normal", color: group === "valid" ? undefined : "#B42318" }}>{group === "valid" ? row.remarks || "—" : row.reason}</Mui.TableCell>
                    </Mui.TableRow>
                  ))}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Mui.Typography sx={{ ...designType.captionText, mt: 1.5 }}>
              Only valid rows are saved, as Ready. Fix invalid rows in the file and upload it again — rows already saved show as Duplicate and are not saved twice.
            </Mui.Typography>
          </SectionCard>
        ) : null}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};
